import React, { useState } from "react";

/** Non-negative decimals, and the half-typed states on the way to one. */
const PARTIAL_DECIMAL = /^\d*\.?\d*$/;

/**
 * A number box that lets a decimal point be typed.
 *
 * A controlled input whose value is round-tripped through `Number()` on every
 * keystroke cannot hold "1." - the dot is not part of a number, so it is dropped
 * the moment it is entered and the next digit lands against the whole part
 * instead. Typing 1.5 gave 15.
 *
 * So the text being typed is kept as text for as long as the box has focus, and
 * only read as a number for the figure it feeds. Leaving the field drops the
 * draft, and the box goes back to showing whatever the number actually is - so
 * a trailing dot or a stray leading zero never survives the edit.
 */
const DecimalInput = ({ value, onChange, onBlur, onFocus, ...rest }) => {
  const [draft, setDraft] = useState(null);

  const shown = draft ?? (value ?? 0);

  const handleChange = (event) => {
    const raw = event.target.value;
    /* Anything that is not on its way to a number is not a keystroke at all -
       the box keeps what it had rather than blanking. */
    if (raw !== "" && !PARTIAL_DECIMAL.test(raw)) return;

    setDraft(raw);
    const parsed = parseFloat(raw);
    onChange?.(Number.isFinite(parsed) ? parsed : 0, raw);
  };

  return (
    <input
      {...rest}
      inputMode="decimal"
      value={shown}
      onChange={handleChange}
      onFocus={(event) => {
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
    />
  );
};

export default DecimalInput;
